import type { Concept } from '@yuan-math/curriculum';

/**
 * Grade 5.
 *
 * `elementary.ts` carries the fraction and decimal spine Grade 5 shares with
 * Grade 4 — equivalence, common denominators, adding and multiplying fractions,
 * decimal place value. This file is the year's own work, and the year has one
 * argument running through it: **the whole number is no longer the unit**.
 *
 * Every topic below is that argument in a different costume. A digit's meaning
 * is a power of ten away from its neighbour's, so multiplying by 100 is a move
 * rather than a calculation. A fraction bar is a division sign, so 17 ÷ 5 has an
 * exact answer and always did. Multiplying can shrink a number and dividing can
 * grow one, once the multiplier is allowed to be less than one. And a solid is
 * measured by filling it rather than by covering it, which needs a third
 * measurement the plane never asked for.
 *
 * Split out rather than merged into `elementary.ts` for the same reason
 * `grade-three.ts` and `grade-four.ts` exist: the band is a cohort, and a
 * reviewer asking "what does Grade 5 cover" should be able to read one file.
 *
 * Every prerequisite here points at something already available, so nothing in
 * this file is reachable-but-unstudiable.
 */
export const GRADE_FIVE_CONCEPTS: Concept[] = [
  /* ------------------------------------------- place value and decimals */
  {
    id: 'powers-of-ten',
    title: 'Powers of Ten',
    summary: 'Multiplying by ten does not calculate anything — it moves every digit one place to the left.',
    strand: 'numbers',
    levels: ['grade-5'],
    prerequisites: ['place-value', 'multiply-by-tens', 'decimal-place-value'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'round-decimals',
    title: 'Rounding Decimals',
    summary: 'The same rule as rounding whole numbers, applied to places that are smaller than one.',
    strand: 'decimals',
    levels: ['grade-5'],
    prerequisites: ['rounding', 'compare-decimals'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'multiply-decimals',
    title: 'Multiplying Decimals',
    summary: 'Multiply as if the points were not there, then count them back in — and expect the answer to be smaller.',
    strand: 'decimals',
    levels: ['grade-5'],
    prerequisites: ['multiply-multi-digit', 'powers-of-ten'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'divide-decimals',
    title: 'Dividing Decimals',
    summary: 'Shift both numbers until the divisor is whole — the quotient does not notice.',
    strand: 'decimals',
    levels: ['grade-5'],
    prerequisites: ['long-division', 'multiply-decimals'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------------------------ fractions */
  {
    id: 'fraction-as-division',
    title: 'A Fraction Is a Division',
    summary: 'The bar has always been a division sign: 3/4 is 3 shared between 4, and a remainder is a fraction waiting to be written.',
    strand: 'fractions',
    levels: ['grade-5'],
    prerequisites: ['fraction-basics', 'division-remainders'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'add-mixed-numbers',
    title: 'Adding and Subtracting Mixed Numbers',
    summary: 'Wholes with wholes and parts with parts — until the parts overflow, or run short, and a whole has to be traded.',
    strand: 'fractions',
    levels: ['grade-5'],
    prerequisites: ['add-fractions', 'mixed-numbers'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'fraction-scaling',
    title: 'Multiplication as Scaling',
    summary: 'You can tell whether a product is bigger or smaller than what you started with, without multiplying anything.',
    strand: 'fractions',
    levels: ['grade-5'],
    prerequisites: ['multiply-fractions', 'compare-fractions'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'divide-unit-fractions',
    title: 'Dividing With Unit Fractions',
    summary: 'How many quarters fit in 3? Twelve — which is why dividing can make a number bigger.',
    strand: 'fractions',
    levels: ['grade-5'],
    prerequisites: ['fraction-as-division', 'multiply-fractions'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* -------------------------------------------------------------- algebra */
  {
    id: 'numerical-expressions',
    title: 'Writing and Reading Expressions',
    summary: 'Brackets are punctuation: they say which part of a calculation is one thought.',
    strand: 'algebra',
    levels: ['grade-5'],
    prerequisites: ['order-of-operations'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'number-pattern-pairs',
    title: 'Two Patterns at Once',
    summary: 'Run two rules side by side and a relationship appears between them that neither rule mentions.',
    strand: 'algebra',
    levels: ['grade-5'],
    prerequisites: ['number-patterns', 'multiplicative-comparison'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* ------------------------------------------------------------- geometry */
  {
    id: 'volume-unit-cubes',
    title: 'Volume by Counting Cubes',
    summary: 'Fill the box with unit cubes and count them — the formula is a shortcut for that count, not a rule to memorise.',
    strand: 'geometry',
    levels: ['grade-5'],
    prerequisites: ['area-rectangles', 'multiply-multi-digit'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'volume-composite',
    title: 'Volume of Combined Solids',
    summary: 'A solid made of two boxes holds what the two boxes hold — provided you never count the same cube twice.',
    strand: 'geometry',
    levels: ['grade-5'],
    prerequisites: ['volume-unit-cubes'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'coordinate-first-quadrant',
    title: 'Plotting Points',
    summary: 'Two number lines at right angles give every point on the page a two-part name — and the order of the parts matters.',
    strand: 'geometry',
    levels: ['grade-5'],
    prerequisites: ['number-pattern-pairs'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'classify-quadrilaterals',
    title: 'Classifying Shapes',
    summary: 'Shape families sit inside one another, and "is a" runs one way only.',
    strand: 'geometry',
    levels: ['grade-5'],
    prerequisites: ['shape-attributes', 'symmetry'],
    status: 'available',
    estimatedMinutes: 25,
  },
];
